import { Injectable } from '@angular/core';
import { LL } from 'i18n/i18n.service';
import { PopupMenuProvider, PopupMenuButton } from 'providers/popup-menu/popup-menu';
import { NavController } from 'providers/nav-controller';
import { TelemetryService } from './telemetry.service';
import { UserDb } from './user-db.service';
import { IBarcodeScannerPage } from 'pages/barcode-scanner-page/barcode-scanner-interface';
import { ILabelScannerPage } from 'pages/label-scanner-page/label-scanner-interface';
import { IWorkoutDetailPage } from 'pages/workout-detail/workout-detail-interface';
import { IEditWeighInPage } from 'pages/edit-weigh-in/edit-weigh-in-interface';
import { DateTime } from 'luxon';
import { LogRecordType, Log_Entry } from '@cf-shared/data-model/user-log';
import { resolveAnyActiveWeeklyRule, Schedule_ItemType } from '@cf-shared/data-model/schedule';
import { ScheduleRuleService } from './schedule-rule.service';
import { DatabaseWrapper, DocWatcher } from './database-wrapper';
import { AlertController, AlertButtonRole } from 'providers/alert-controller';
import { hoursToIsoTime, isoTimeToHours } from '@cf-shared/time';
import { DebugService } from './debug.service';
import { LoggingService } from './logging.service';
import { IFoodsPage } from 'pages/foods/foods-interface';
import { ConfigDbService } from './config-db.service';
import { ConfigKey } from './config-keys';

@Injectable()
export class ActivityMenuService {
  constructor(
    private popupMenuProvider: PopupMenuProvider,
    private navCtrl: NavController,
    private telemetry: TelemetryService,
    private userDb: UserDb,
    private alertCtrl: AlertController,
    private debug: DebugService,
    private logging: LoggingService,
    private scheduleRuleService: ScheduleRuleService,
    private configDb: ConfigDbService,
  ) {}

  /**
   * Show the activity menu (Log Food, Add Workout)
   * @param targetElement - The HTML element to anchor the popup to
   * @param currentDay - The current day for context (optional, defaults to today)
   * @param logEntries - Log entries to check if workout already exists (optional)
   */
  async showActivityMenu(
    targetElement: HTMLElement,
    watcher: DocWatcher,
    currentDay?: DateTime,
    logEntries?: Log_Entry[]
  ): Promise<void> {
    if (!currentDay) {
      currentDay = this.debug.getNowLocal().startOf('day');
    }

    // if log entries not provided, load them ourselves
    if (!logEntries) {
      logEntries = await this.userDb.getLogEntriesInRangeAsync(
        currentDay.startOf('day'),
        currentDay.endOf('day'),
        watcher,
        { inLogEntriesTimeZone: true }
      );
    }

    const buttons: PopupMenuButton[] = [];

    // add weigh in button - should always have a weigh-in entry
    const weighInEntry = logEntries?.find(entry => entry.type === LogRecordType.WeighIn);
    buttons.push({
      text: LL.home.buttons.weighIn(),
      icon: 'monitor-weight',
      handler: () => {
        if (weighInEntry) {
          // open the existing weigh-in
          void this.navCtrl.pushAsync(IEditWeighInPage, {
            id: weighInEntry._id,
            day: currentDay.toISODate()
          });
        } else {
          // this shouldn't happen - weigh-ins should always exist
          this.logging.trackSilentProblem('No weigh-in entry found for current day', {
            currentDay: currentDay.toISODate(),
            entriesCount: logEntries?.length || 0
          });
        }
      }
    });

    // add log workout button
    const workoutEntry = logEntries?.find(entry => entry.type === LogRecordType.Workout);
    buttons.push({
      text: LL.home.buttons.logWorkout(),
      icon: 'barbell',
      handler: async () => {
        if (workoutEntry) {
          // if workout already exists, open it
          void this.navCtrl.pushAsync(IWorkoutDetailPage, {
            id: workoutEntry._id
          });
        } else {
          // if no workout exists, add one
          await this.addWorkout(currentDay);
        }
      }
    });

    // add log meal button
    buttons.push({
      text: LL.home.buttons.logMeal(),
      icon: 'food',
      handler: () => {
        this.telemetry.trackEvent('home_log_food', {});
        void this.navCtrl.pushAsync(IFoodsPage, {
          mode: 'select',
          gotoLogFoodAfterSelect_dayDate: currentDay.toISODate(),
          openBarcodeScanner: false
        });
      }
    });

    // add barcode scan button
    buttons.push({
      text: LL.home.buttons.barcodeScan(),
      icon: 'barcode',
      handler: () => {
        this.telemetry.trackEvent('home_barcode_scan', {});
        void this.navCtrl.pushAsync(IBarcodeScannerPage, {
          gotoFoodsAfterScan_dayDate: currentDay.toISODate()
        });
      }
    });

    // if any scanner model is configured, add scan label button (#3783)
    const scanProvider = this.configDb.getConfigValue<string>(ConfigKey.ScanProvider);
    if (scanProvider) {
      buttons.push({
        text: LL.home.buttons.labelScan(),
        icon: 'camera',
        handler: () => {
          this.telemetry.trackEvent('home_scan_label', {});
          // Straight to the scanner, carrying the day (#5377). The day is what makes the scan
          // end somewhere: the food editor the scanner turns into opens the portion screen for
          // that day in its own place, so Log lands back on the page this menu was opened from.
          // Without it (before #4968) a scan ended at a saved food with nothing logged. It used
          // to go through the food list to carry the day, which showed the user a search list
          // they never used; the owner confirmed on 2026-09-08 that Log should not land on it.
          void this.navCtrl.pushAsync(ILabelScannerPage, {
            gotoLogFoodAfterSelect_dayDate: currentDay.toISODate(),
          });
        }
      });
    }

    await this.popupMenuProvider.createMenu({
      buttons,
      targetFromElement: targetElement,
    });
  }

  private async addWorkout(currentDay: DateTime): Promise<void> {
    const schedule = await this.userDb.loadScheduleAsync();
    // the user's weekly workout time, from whichever weekly rule is in force that day —
    // asked this way so the answer does not depend on the order the rules are stored in
    const workoutRule = resolveAnyActiveWeeklyRule(
      schedule.rules ?? [], Schedule_ItemType.Workout, currentDay.toISODate());

    let workoutTime: string;
    if (workoutRule) {
      workoutTime = hoursToIsoTime(workoutRule.time_hr);
    } else {
      // default to a reasonable time if no rule exists
      workoutTime = '10:00';
    }

    workoutTime = await this.alertCtrl.showAlertAsync<string>({
      inputs: [{
        type: 'time',
        name: 'workoutTime',
        value: workoutTime,
        label: LL.home.alerts.workoutTimeLabel()
      }],
      buttons: [
        {
          text: LL.common.buttons.ok(),
          role: AlertButtonRole.Primary,
          result: data => data['workoutTime'],
        },
        {
          text: LL.common.buttons.cancel(),
          role: AlertButtonRole.Cancel
        },
      ]
    });

    if (workoutTime) {
      const timeHr = isoTimeToHours(workoutTime);
      const todayIso = currentDay.toISODate();
      this.scheduleRuleService.addOneTimeItem({
        rules: schedule.rules || [],
        itemType: Schedule_ItemType.Workout,
        dateISO: todayIso,
        time_hr: timeHr,
      });

      await this.userDb.saveScheduleAsync(DatabaseWrapper.getTransactionId());
      await this.userDb.clearBacklogCacheAsync();
      this.telemetry.trackEvent('home_add_workout', {});
    }
  }
}